"use client";

import React, { useState, useRef, useLayoutEffect } from "react";
import { cn } from "@/utils";
import { Input } from "@/components/ui/input";
import { ChevronDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";

export function TaxonomyFilter({
  className,
  items,
  selected,
  setSelected,
  counts,
  slugKey,
  nounPlural,
  nounSingular,
}: {
  className?: string;
  items: any;
  selected: string[];
  setSelected: (value: string[] | null) => Promise<URLSearchParams>;
  counts: Record<string, number>;
  slugKey: "categorySlug" | "tagSlug";
  nounPlural: string;
  nounSingular: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");

  const pillsContainerRef = useRef<HTMLDivElement>(null);
  const pillsMeasureRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(selected.length);
  const [triggerWidth, setTriggerWidth] = useState<number | undefined>();

  const handleToggle = (slug: string) => {
    if (selected.includes(slug)) {
      const next = selected.filter((c) => c !== slug);
      setSelected(next.length === 0 ? null : next);
    } else {
      setSelected([...selected, slug]);
    }
  };

  const handleRemove = (slug: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const next = selected.filter((c) => c !== slug);
    setSelected(next.length === 0 ? null : next);
  };

  const handleClearAll = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelected(null);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) setQuery("");
  };

  useLayoutEffect(() => {
    const container = pillsContainerRef.current;
    const measure = pillsMeasureRef.current;
    if (!container || !measure) return;

    const gap = 6;
    const triggerPadding = 16;
    const minTriggerWidth = 260;
    const maxTriggerWidth = 320;

    const update = () => {
      const children = Array.from(measure.children) as HTMLElement[];
      if (children.length === 0) {
        setVisibleCount(0);
        setTriggerWidth(undefined);
        return;
      }

      const overflowWidth = children[children.length - 1].offsetWidth;
      const pillWidths = children.slice(0, -1).map((el) => el.offsetWidth);
      const actionsWidth = actionsRef.current?.offsetWidth ?? 52;
      const parentWidth = rootRef.current?.parentElement?.clientWidth;
      const maxWidth = Math.min(
        maxTriggerWidth,
        parentWidth && parentWidth > minTriggerWidth ? parentWidth : maxTriggerWidth
      );

      const pillsTotal = pillWidths.reduce(
        (sum, width, index) => sum + width + (index > 0 ? gap : 0),
        0
      );
      const desired = pillsTotal + actionsWidth + triggerPadding + gap;

      if (pillWidths.length <= 1 || desired <= maxWidth) {
        setVisibleCount(pillWidths.length);
        setTriggerWidth(Math.min(maxWidth, Math.max(minTriggerWidth, desired)));
        return;
      }

      setTriggerWidth(maxWidth);
      const available = maxWidth - actionsWidth - triggerPadding - gap;

      let used = 0;
      let count = 0;
      for (let i = 0; i < pillWidths.length; i++) {
        const needsOverflow = i < pillWidths.length - 1;
        const next =
          used + (count > 0 ? gap : 0) + pillWidths[i] + (needsOverflow ? gap + overflowWidth : 0);
        if (next > available) break;
        used += (count > 0 ? gap : 0) + pillWidths[i];
        count += 1;
      }

      setVisibleCount(Math.max(1, count));
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(measure);
    if (rootRef.current?.parentElement) {
      observer.observe(rootRef.current.parentElement);
    }
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [selected, items]);

  const visibleSelected = selected.slice(0, visibleCount);
  const remainingCount = Math.max(0, selected.length - visibleCount);

  const getTitle = (slug: string) => {
    const match = items?.find((item) => item.node?.[slugKey] === slug);
    return match?.node?.title || slug;
  };

  const visibleList = React.useMemo(() => {
    const list = (items || []).filter((item) => {
      const slug = item.node?.[slugKey];
      const count = counts[slug] ?? 0;
      return count > 0 || selected.includes(slug);
    });
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((item) => {
      const title = item.node?.title?.toLowerCase() || "";
      const slug = item.node?.[slugKey]?.toLowerCase() || "";
      return title.includes(q) || slug.includes(q);
    });
  }, [items, query, slugKey, counts, selected]);

  const searchInputId = `${nounSingular}-search-input`;

  return (
    <div
      ref={rootRef}
      className={cn(
        "flex justify-end items-center gap-2 md:ml-auto mb-auto w-auto",
        selected.length > 0 && "shrink-0",
        className
      )}
    >
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <Button
          asChild
          variant="outline"
          className={cn(
            "!h-11 !py-0 gap-1.5 min-w-[260px] w-full !border-[1px]",
            selected.length === 0 ? "px-5 md:max-w-[300px]" : "!px-2 md:max-w-[460px]",
            isOpen ? "border-primary-200" : ""
          )}
          style={
            selected.length > 0 && triggerWidth
              ? { width: `${triggerWidth}px`, maxWidth: "100%" }
              : undefined
          }
        >
          <PopoverTrigger className="!border-0 outline-none ring-0">
            <div className="flex items-center gap-1.5 flex-1 w-full min-w-0">
              {selected.length === 0 ? (
                <div className="flex-1 flex items-center justify-between">
                  <span className="opacity-90">Filter by {nounPlural}</span>
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </div>
              ) : (
                <>
                  <div
                    ref={pillsContainerRef}
                    className="relative flex-1 flex items-center gap-1.5 min-w-0 overflow-hidden"
                  >
                    <div
                      ref={pillsMeasureRef}
                      aria-hidden
                      className="absolute left-0 top-0 flex items-center gap-1.5 whitespace-nowrap opacity-0 pointer-events-none"
                    >
                      {selected.map((slug) => (
                        <Badge
                          key={`measure-${slug}`}
                          variant="outline"
                          className="h-6 rounded-full !border-primary-200 !text-primary-200 bg-primary-200/10 px-2 py-0 gap-1 max-w-[220px] shrink-0 light:!border-primary-600 light:!text-primary-600 light:!bg-primary-200/10"
                        >
                          <span className="truncate">{getTitle(slug)}</span>
                          <span className="inline-flex p-0.5 -mr-0.5">
                            <X className="!h-3 !w-3" aria-hidden />
                          </span>
                        </Badge>
                      ))}
                      <Badge
                        variant="outline"
                        className="h-6 rounded-full !border-primary-200 !text-primary-200 bg-primary-200/10 px-2 py-0 shrink-0"
                      >
                        +{selected.length}
                      </Badge>
                    </div>
                    {visibleSelected.map((slug) => (
                      <Badge
                        key={slug}
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        className="h-6.5 rounded-full !border-primary-200 !text-primary-200 bg-background px-3 py-0 gap-1 max-w-[220px] min-w-0 shrink light:!border-primary-600 light:!text-primary-600 light:!bg-primary-200/10"
                      >
                        <span className="truncate">{getTitle(slug)}</span>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center rounded-full p-0.5 -mr-0.5 hover:bg-primary-200/15 light:hover:!bg-primary-200/20"
                          aria-label={`Remove ${getTitle(slug)}`}
                          onClick={(e) => handleRemove(slug, e)}
                        >
                          <X className="!h-3 !w-3" aria-hidden />
                        </button>
                      </Badge>
                    ))}

                    {remainingCount > 0 && (
                      <Badge
                        variant="outline"
                        className="h-6.5 rounded-full !border-primary-200 !text-primary-200 bg-background px-2 py-0 shrink-0 light:!border-primary-600 light:!text-primary-600 light:!bg-primary-200/10"
                      >
                        +{remainingCount}
                      </Badge>
                    )}
                  </div>
                  <div
                    ref={actionsRef}
                    className="flex-shrink-0 flex items-center gap-1 pr-1"
                  >
                    <button
                      type="button"
                      onClick={handleClearAll}
                      className="rounded-sm hover:bg-gray-700 light:hover:!bg-gray-200 p-1 transition-colors"
                      aria-label={`Clear all ${nounSingular} filters`}
                    >
                      <X className="h-3.5 w-3.5" aria-hidden />
                    </button>
                    <ChevronDown className="h-4 w-4 opacity-50" />
                  </div>
                </>
              )}
            </div>
          </PopoverTrigger>
        </Button>
        <PopoverContent
          className="w-[280px] p-0 overflow-hidden rounded-xl bg-background  light:!bg-white border-border"
          align="end"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="p-3 pb-2.5 border-b border-gray-700 light:!border-gray-200">
            <label htmlFor={searchInputId} className="sr-only">
              Search {nounPlural.toLowerCase()}...
            </label>
            <Input
              id={searchInputId}
              size="xs"
              placeholder={`Search ${nounPlural}...`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="max-h-[280px] overflow-y-auto py-1.5 px-1.5 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-600 light:[&::-webkit-scrollbar-thumb]:bg-gray-300">
            {visibleList.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs font-mono uppercase text-gray-500">
                No {nounPlural.toLowerCase()}
              </div>
            ) : (
              visibleList.map((item) => {
                const slug = item.node[slugKey];
                const isSelected = selected.includes(slug);
                const count = counts[slug] ?? 0;
                return (
                  <label
                    key={item.node.id}
                    className="flex items-center gap-3 cursor-pointer hover:bg-white/5 light:hover:!bg-gray-100 px-2 py-2 rounded-md"
                    htmlFor={item.node.id}
                  >
                    <Checkbox
                      id={item.node.id}
                      checked={isSelected}
                      onCheckedChange={() => handleToggle(slug)}
                      className={cn(
                        "size-3.5 rounded-[3px] border-gray-500 bg-transparent shadow-none",
                        "data-[state=checked]:!bg-primary-200 data-[state=checked]:!border-primary-200 data-[state=checked]:!text-[#1a1a1a]",
                        "light:!border-gray-400 light:data-[state=checked]:!bg-primary-200 light:data-[state=checked]:!text-[#1a1a1a]"
                      )}
                    />
                    <span
                      className={cn(
                        "flex-1 min-w-0 truncate text-[13px] font-mono uppercase tracking-wide select-none",
                        isSelected
                          ? "text-primary-200 light:!text-primary-600"
                          : "text-gray-400 light:!text-gray-600"
                      )}
                    >
                      {item.node.title}
                    </span>
                    <span className="shrink-0 text-xs font-mono tabular-nums text-gray-500 light:!text-gray-400">
                      {count}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
